import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, ArrowLeft } from "lucide-react";
import { plans } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StartWizard = () => {
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get("plan") || "care";
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdSlug, setCreatedSlug] = useState("");
  const [form, setForm] = useState({
    plan: plans.some((p) => p.id === initialPlan) ? initialPlan : "care",
    personFirstName: "",
    personLastName: "",
    slug: "",
    story: "",
    yourName: "",
    yourEmail: "",
    relationship: "",
    passcode: "",
  });

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "personFirstName" || key === "personLastName") {
        const first = key === "personFirstName" ? value : f.personFirstName;
        const last = key === "personLastName" ? value : f.personLastName;
        next.slug = `${first} ${last}`.trim().toLowerCase().replace(/\s+/g, "-");
      }
      return next;
    });
  };

  const canContinue2 = form.personFirstName.trim() && form.slug.trim();
  const canSubmit =
    form.yourName.trim() && /\S+@\S+\.\S+/.test(form.yourEmail) && form.passcode.trim().length >= 4;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast({
        title: "Almost there",
        description: "Please add your name, a valid email, and a passcode (4+ characters).",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/start-requests`, form);
      setCreatedSlug(res.data.slug);
      setSubmitted(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast({
        title: "Couldn't create the page",
        description:
          e.response?.status === 409
            ? "That page name is already taken — try another."
            : typeof detail === "string"
            ? detail
            : "Please check the details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-2xl bg-secondary/60 border-0 px-4 text-base md:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  if (submitted) {
    return (
      <main className="min-h-screen bg-gradient-warm">
        <section className="mx-auto max-w-2xl px-6 py-24 text-center animate-fade-up">
          <div className="rounded-3xl bg-card shadow-card p-10">
            <Heart className="h-8 w-8 text-primary fill-primary mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-foreground">
              {form.personFirstName || "Your person"}'s page is live.
            </h1>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
              Share the link and passcode with the people who love {form.personFirstName || "them"}.
              We've also noted <span className="text-foreground">{form.yourEmail}</span> as the family contact.
            </p>
            <div className="mt-6 rounded-2xl bg-secondary/60 px-4 py-3 text-sm text-foreground" data-testid="created-link">
              openhart.us/<span className="font-medium">{createdSlug}</span>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Family admin passcode: <span className="text-foreground font-medium">{form.passcode}-admin</span>{" "}
              — use it at /{createdSlug}/admin to post updates, needs, and appointments.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={`/${createdSlug}`}
                data-testid="view-page-link"
                className="inline-flex items-center justify-center rounded-full bg-gradient-rose text-primary-foreground px-6 py-2.5 text-sm font-medium shadow-soft hover:shadow-glow transition-gentle"
              >
                View the page
              </Link>
              <Link
                to="/"
                data-testid="back-home-link"
                className="inline-flex items-center justify-center rounded-full bg-primary-soft text-primary px-6 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
              >
                Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-warm">
      <section className="mx-auto max-w-2xl px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-gentle mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>
        <div className="rounded-3xl bg-card shadow-card p-8 sm:p-10">
          {/* Progress */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-xs uppercase tracking-[0.25em] text-primary">Step {step} of 3</div>
            <div className="flex gap-1.5">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 w-8 rounded-full transition-gentle ${s <= step ? "bg-primary" : "bg-primary-soft"}`}
                />
              ))}
            </div>
          </div>

          {step === 1 && (
            <div>
              <h1 className="font-serif text-3xl text-foreground">Choose your plan</h1>
              <p className="mt-2 text-sm text-muted-foreground">You can change this later. Most families start with Care.</p>
              <div className="mt-6 space-y-3">
                {plans.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`wizard-plan-${p.id}`}
                    onClick={() => setForm((f) => ({ ...f, plan: p.id }))}
                    className={`w-full text-left rounded-2xl border-2 p-4 transition-gentle ${
                      form.plan === p.id
                        ? "border-primary bg-primary-soft/40"
                        : "border-border bg-background hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-serif text-xl text-foreground">{p.name}</div>
                      <div className="text-sm text-foreground">
                        <span className="font-serif text-lg">{p.price}</span>{" "}
                        <span className="text-xs text-muted-foreground">{p.period}</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{p.tagline}</p>
                    <p className="mt-1 text-[11px] italic text-muted-foreground">+ One-time deployment fee (determined after consultation)</p>
                  </button>
                ))}
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  type="button"
                  data-testid="wizard-continue-1"
                  onClick={() => setStep(2)}
                  className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-medium hover:bg-primary/90 transition-gentle"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-serif text-3xl text-foreground">Tell us about your person</h1>
              <p className="mt-2 text-sm text-muted-foreground">This is who the page is for. Share as much or as little as you like.</p>
              <div className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">First name</label>
                    <input
                      className={`${inputCls} mt-1.5`}
                      placeholder="Ann"
                      data-testid="person-first-name"
                      value={form.personFirstName}
                      onChange={set("personFirstName")}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Last name</label>
                    <input
                      className={`${inputCls} mt-1.5`}
                      placeholder="Fisher"
                      data-testid="person-last-name"
                      value={form.personLastName}
                      onChange={set("personLastName")}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Their page name</label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">openhart.us/</span>
                    <input
                      className={`${inputCls} flex-1`}
                      placeholder="ann-fisher"
                      data-testid="person-slug"
                      value={form.slug}
                      onChange={set("slug")}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Their story (optional)</label>
                  <textarea
                    className="mt-1.5 w-full rounded-2xl bg-secondary/60 border-0 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 min-h-[110px] resize-none"
                    placeholder="A few words about who they are and what's happening…"
                    data-testid="person-story"
                    value={form.story}
                    onChange={set("story")}
                  />
                </div>
              </div>
              <div className="mt-8 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="rounded-full bg-secondary text-foreground px-6 py-2.5 text-sm font-medium hover:bg-secondary/70 transition-gentle"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  data-testid="wizard-continue-2"
                  disabled={!canContinue2}
                  onClick={() => setStep(3)}
                  className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-50 disabled:pointer-events-none"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-serif text-3xl text-foreground">And a little about you</h1>
              <p className="mt-2 text-sm text-muted-foreground">So we know where to send the page once it's ready.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your name</label>
                  <input
                    className={`${inputCls} mt-1.5`}
                    placeholder="Sarah Fisher"
                    data-testid="your-name"
                    value={form.yourName}
                    onChange={set("yourName")}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your email</label>
                  <input
                    type="email"
                    className={`${inputCls} mt-1.5`}
                    placeholder="sarah@example.com"
                    data-testid="your-email"
                    value={form.yourEmail}
                    onChange={set("yourEmail")}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your relationship to them (optional)</label>
                  <input
                    className={`${inputCls} mt-1.5`}
                    placeholder="Daughter, friend, neighbor…"
                    data-testid="your-relationship"
                    value={form.relationship}
                    onChange={set("relationship")}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Passcode for the page</label>
                  <input
                    className={`${inputCls} mt-1.5`}
                    placeholder="At least 4 characters — share it with your circle"
                    data-testid="page-passcode"
                    value={form.passcode}
                    onChange={set("passcode")}
                  />
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    Visitors will need this to open the page. Keep it simple and memorable.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded-full bg-secondary text-foreground px-6 py-2.5 text-sm font-medium hover:bg-secondary/70 transition-gentle"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  data-testid="wizard-submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-full bg-gradient-rose text-primary-foreground px-6 py-2.5 text-sm font-medium shadow-soft hover:shadow-glow transition-gentle disabled:opacity-60"
                >
                  {submitting ? "Creating…" : "Create the page"}
                </button>
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">
          You won't be charged today. We'll set up your page first, then send the link.
        </p>
      </section>
    </main>
  );
};

export default StartWizard;
